import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Loader2, Save, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const toMin = (t) => { const [h, m] = t.split(":"); return +h * 60 + +m; };

// ---------- Onglet Volumes horaires ----------
export const VolumesTab = ({ selectedClass }) => {
  const [volumes, setVolumes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ subject: "", hours_per_week: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!selectedClass) return;
    try {
      const [v, s] = await Promise.all([
        api.get("/timetable/volumes", { params: { class_name: selectedClass } }),
        api.get("/timetable", { params: { class_name: selectedClass } }),
      ]);
      setVolumes(v.data); setSlots(s.data);
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { load(); }, [selectedClass]);

  const plannedFor = (subject) => slots
    .filter(s => s.subject.trim().toLowerCase() === subject.trim().toLowerCase())
    .reduce((sum, s) => sum + (toMin(s.end_time) - toMin(s.start_time)) / 60, 0);

  const save = async () => {
    if (!form.subject.trim() || !form.hours_per_week) return toast.error("Matière et volume obligatoires");
    setSaving(true);
    try {
      await api.post("/timetable/volumes", { class_name: selectedClass, subject: form.subject, hours_per_week: parseFloat(form.hours_per_week) });
      toast.success("Volume horaire enregistré");
      setForm({ subject: "", hours_per_week: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const del = async (v) => {
    if (!window.confirm(`Supprimer le volume ${v.subject} (${v.hours_per_week}h/sem) ?`)) return;
    try { await api.delete(`/timetable/volumes/${v.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="volumes-tab">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <Label>Matière</Label>
          <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Mathématiques" data-testid="volume-subject" />
        </div>
        <div className="w-36">
          <Label>Heures / semaine</Label>
          <Input type="number" step="0.5" min="0.5" max="40" value={form.hours_per_week} onChange={e => setForm({ ...form, hours_per_week: e.target.value })} placeholder="5" data-testid="volume-hours" />
        </div>
        <Button onClick={save} disabled={saving || !selectedClass} className="bg-[#1E3A8A] text-white gap-1" data-testid="volume-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" />Définir</>}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">
          Volumes horaires — {selectedClass || "sélectionnez une classe"}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Matière</th>
              <th className="px-4 py-2 text-right">Volume requis</th>
              <th className="px-4 py-2 text-right">Planifié</th>
              <th className="px-4 py-2 text-left">Statut</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {volumes.map(v => {
              const planned = plannedFor(v.subject);
              const complete = Math.abs(planned - v.hours_per_week) < 0.01;
              return (
                <tr key={v.id} className="border-t border-slate-100" data-testid={`volume-row-${v.id}`}>
                  <td className="px-4 py-2 font-semibold text-slate-800">{v.subject}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{v.hours_per_week}h/sem</td>
                  <td className="px-4 py-2 text-right tabular-nums">{planned.toFixed(1)}h</td>
                  <td className="px-4 py-2">
                    {complete
                      ? <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700"><CheckCircle2 className="h-3.5 w-3.5" />Complet</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600"><AlertTriangle className="h-3.5 w-3.5" />{planned < v.hours_per_week ? `Incomplet (reste ${(v.hours_per_week - planned).toFixed(1)}h)` : "Dépassement"}</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => del(v)} className="text-red-400 hover:text-red-600" data-testid={`delete-volume-${v.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}
            {volumes.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Aucun volume défini pour cette classe. Le volume limite la planification hebdomadaire de chaque matière.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Onglet Suivi des séances ----------
const REPORT_BADGES = {
  cours_valide: ["Cours validé", "bg-green-100 text-green-700"],
  cours_valide_regularise: ["Validé (régularisé)", "bg-teal-100 text-teal-700"],
  cours_non_termine: ["Non terminé", "bg-red-100 text-red-700"],
  en_cours: ["En cours", "bg-blue-100 text-blue-700"],
  non_pointee: ["Non pointée", "bg-red-100 text-red-700"],
  a_venir: ["À venir", "bg-slate-100 text-slate-500"],
};

export const SuiviSeancesTab = () => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);

  const load = async (d) => {
    try {
      const { data } = await api.get("/timetable/sessions-report", { params: { date: d } });
      setReport(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => { load(date); }, [date]);

  const S = report?.summary || {};
  const cards = [
    ["Prévues", S.prevues, "text-[#1E3A8A]"],
    ["Effectuées", S.effectuees, "text-green-700"],
    ["En cours", S.en_cours, "text-blue-600"],
    ["Non pointées", S.non_pointees, "text-red-600"],
    ["Retards", S.retards, "text-amber-600"],
  ];

  return (
    <div className="space-y-4" data-testid="suivi-seances-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" data-testid="report-date" />
        <span className="text-sm text-slate-500">{report?.day}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {cards.map(([label, val, cls]) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-3 text-center">
            <div className={`text-2xl font-extrabold tabular-nums ${cls}`}>{val ?? 0}</div>
            <div className="text-[11px] uppercase text-slate-500">{label}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Horaire</th>
              <th className="px-4 py-2 text-left">Classe</th>
              <th className="px-4 py-2 text-left">Matière</th>
              <th className="px-4 py-2 text-left">Enseignant</th>
              <th className="px-4 py-2 text-left">Statut</th>
              <th className="px-4 py-2 text-right">Retard</th>
              <th className="px-4 py-2 text-right">Min. validées</th>
            </tr>
          </thead>
          <tbody>
            {(report?.rows || []).map(r => {
              const [label, cls] = REPORT_BADGES[r.report_status] || [r.report_status, "bg-slate-100 text-slate-500"];
              return (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`report-row-${r.id}`}>
                  <td className="px-4 py-2 font-mono text-xs font-bold text-[#166534]">{r.start_time}–{r.end_time}</td>
                  <td className="px-4 py-2">{r.class_name}</td>
                  <td className="px-4 py-2 font-semibold">{r.subject}</td>
                  <td className="px-4 py-2 text-slate-600">{r.teacher_name || "—"}</td>
                  <td className="px-4 py-2"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span></td>
                  <td className="px-4 py-2 text-right tabular-nums text-xs">{r.session?.retard_min ? `${r.session.retard_min} min` : "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-xs">{r.session?.validated_minutes != null ? Math.round(r.session.validated_minutes) : "—"}</td>
                </tr>
              );
            })}
            {(report?.rows || []).length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Aucune séance prévue ce jour.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Onglet Règles de tolérance ----------
export const ReglesTab = () => {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/timetable/session-rules").then(({ data }) => setRules(data)).catch(() => toast.error("Erreur"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/timetable/session-rules", {
        open_before_min: +rules.open_before_min,
        late_tolerance_min: +rules.late_tolerance_min,
        max_start_delay_min: +rules.max_start_delay_min,
      });
      toast.success("Règles enregistrées");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!rules) return null;
  const fields = [
    ["open_before_min", "Ouverture du pointage avant le cours (min)", "L'enseignant peut démarrer le cours X minutes avant l'heure prévue."],
    ["late_tolerance_min", "Tolérance de retard (min)", "Au-delà, la séance est marquée « en retard »."],
    ["max_start_delay_min", "Délai maximal de démarrage (min)", "Au-delà, le pointage est refusé et la séance devient « non pointée »."],
  ];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl space-y-5" data-testid="regles-tab">
      {fields.map(([key, label, help]) => (
        <div key={key}>
          <Label className="font-semibold">{label}</Label>
          <Input type="number" min="0" max="120" value={rules[key]} onChange={e => setRules({ ...rules, [key]: e.target.value })} className="w-32 mt-1" data-testid={`rule-${key}`} />
          <p className="text-xs text-slate-500 mt-1">{help}</p>
        </div>
      ))}
      <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] text-white gap-1" data-testid="rules-submit">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" />Enregistrer les règles</>}
      </Button>
    </div>
  );
};
